import * as React from 'react';
import { useId } from 'react';

import Switch from '../../../../core/components/Switch.tsx';
import classnames from '../../../../core/utils/classnames.tsx';

interface ToggleCardProps extends Omit<React.ComponentPropsWithRef<'div'>, 'title'> {
    icon?: React.ReactNode;
    title: React.ReactNode;
    description?: React.ReactNode;
    descriptionClassName?: string;
    isChecked: boolean;
    onIsCheckedChange: (isChecked: boolean) => void;
    isDisabled?: boolean;
    panel?: React.ReactNode;
}

const ToggleCard = ({
    icon,
    title,
    description,
    descriptionClassName,
    isChecked,
    onIsCheckedChange,
    isDisabled = false,
    panel,
    className,
    children,
    ...props
}: ToggleCardProps) => {
    const titleId = useId();
    const descriptionId = useId();

    return (
        <div
            {...props}
            className={classnames(
                'flex flex-col gap-2 rounded-lg border border-solid border-line-weak bg-surface-weak p-4',
                className
            )}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-2">
                    {icon && (
                        <span className="flex h-5 w-4 flex-none items-center justify-center text-content-medium">
                            {icon}
                        </span>
                    )}
                    <span id={titleId} className="min-w-0 break-words text-sm font-medium text-content-strong">
                        {title}
                    </span>
                </div>
                {/* Switch renders its own overflow-hidden wrapper, so keep it from shrinking next to a long title */}
                <div className="flex-none">
                    <Switch
                        value={isChecked}
                        onValueChange={onIsCheckedChange}
                        disabled={isDisabled}
                        aria-labelledby={titleId}
                        aria-describedby={description ? descriptionId : undefined}
                    />
                </div>
            </div>
            {description && (
                <p id={descriptionId} className={classnames('m-0 text-sm text-content-medium', descriptionClassName)}>
                    {description}
                </p>
            )}
            {children}
            {panel && <div className="mt-2 flex flex-col gap-4 rounded-lg bg-surface-medium p-3">{panel}</div>}
        </div>
    );
};

export default ToggleCard;
